/**
 * Serviço HTTP centralizado para todas as chamadas de API
 * Centraliza autenticação, retry, tratamento de erros e cache
 */

import { safeStringify } from "../utils/safeStringify";
import { ErrorHandler } from "../utils/errorHandler";
import { retryMechanism } from "../utils/retryMechanism";
import { getActiveContextForApi } from "./activeContextHolder";
import { showToastWarning } from "./alertService";
import { clearAuthStorage, redirectToLogin } from "./authUtils";
import {
  API_BASE_URL,
  API_ENDPOINTS,
  STORAGE_KEYS,
  TIMEOUTS,
} from "./constants";

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  [key: string]: any;
}

export interface ApiConfig {
  requiresAuth?: boolean;
  timeout?: number;
  retry?: boolean;
  cache?: boolean;
  cacheTtl?: number;
}

class ApiService {
  private tokenRenewer: (() => Promise<boolean>) | null = null;

  public setTokenRenewer(renewer: () => Promise<boolean>) {
    this.tokenRenewer = renewer;
  }

  private getAuthToken(): string | null {
    return (
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      localStorage.getItem(STORAGE_KEYS.TOKEN)
    );
  }

  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number>,
  ): string {
    // Se o endpoint já for uma URL absoluta, retorná-lo após substituir params
    if (endpoint.startsWith("http")) {
      let url = endpoint;

      // Substituir parâmetros na URL
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          url = url.replace(`{${key}}`, String(value));
        });
      }

      return url;
    }

    // Normalizar para evitar duplicação de prefixos como "/api/api/..."
    const base = API_BASE_URL.endsWith("/")
      ? API_BASE_URL.slice(0, -1)
      : API_BASE_URL;
    const ep = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

    // Caso comum: API_BASE_URL já termina com "/api" e o endpoint também começa com "/api"
    // Ex.: base="http://host:8000/api" + endpoint="/api/ads/..." => deveria virar "/api/ads/..."
    let url: string;
    if (
      ep.toLowerCase().startsWith("/api") &&
      base.toLowerCase().endsWith("/api")
    ) {
      url = `${base}${ep.substring(4)}`; // remove o "/api" inicial do endpoint
    } else {
      url = `${base}${ep}`;
    }

    // Substituir parâmetros na URL
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, String(value));
      });
    }

    // Log da URL construída para debug em desenvolvimento ou produção se necessário
    if (process.env.NODE_ENV === "development") {
      console.log("🌐 [API SERVICE] Request URL:", url);
    }

    return url;
  }

  private buildHeaders(requiresAuth: boolean = true): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const token = this.getAuthToken();
    if (token && requiresAuth) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const ctx = getActiveContextForApi();
    if (ctx) {
      headers["X-Active-Context"] = ctx.type;
      headers["X-Active-Context-Id"] = String(
        ctx.type === "ORGANIZATION"
          ? (ctx.organizationId ?? "")
          : (ctx.clientCompanyId ?? ""),
      );

      // Suporte para impersonação de System Admin
      if (ctx.adminOrganizationId) {
        headers["X-Admin-Organization-Id"] = String(ctx.adminOrganizationId);
      }
    }

    return headers;
  }

  private buildConfig(
    method: string,
    headers: Record<string, string>,
    data?: any,
    config?: ApiConfig,
  ): RequestInit {
    // Detectar se é localhost (desenvolvimento ou build local)
    const isLocalhost =
      API_BASE_URL.includes("localhost") || API_BASE_URL.includes("127.0.0.1");

    const requestConfig: RequestInit = {
      method,
      headers,
      mode: "cors",
      // Em localhost, omitir credentials (não usar 'include' ou 'same-origin')
      // Isso evita problemas de CORS quando backend não suporta credentials
      // Em produção remota, usar credentials para cookies/sessões
      ...(isLocalhost ? {} : { credentials: "include" }),
    };

    // Adicionar body se houver dados e não for GET
    if (data && method !== "GET") {
      if (data instanceof FormData) {
        // Para FormData, remover Content-Type (deixe o browser definir)
        delete headers["Content-Type"];
        requestConfig.body = data;
      } else {
        requestConfig.body = safeStringify(data);
      }
    }

    // Adicionar timeout se especificado
    if (config?.timeout) {
      const controller = new AbortController();
      requestConfig.signal = controller.signal;

      // Timeout após o tempo especificado
      setTimeout(() => controller.abort(), config.timeout);
    }

    return requestConfig;
  }

  private async handleResponse<T>(
    response: Response,
    config?: ApiConfig,
  ): Promise<T> {
    // Ler o body uma única vez e armazenar
    let responseBody: any = null;
    const extractBackendErrorMessage = (payload: any): string | undefined => {
      if (!payload) return undefined;

      // Formato comum: { error: "..." } ou { message: "..." }
      if (typeof payload.error === "string" && payload.error.trim())
        return payload.error;
      if (typeof payload.message === "string" && payload.message.trim())
        return payload.message;

      // Formato estruturado: { error: { error_user_msg, error_user_title, message } }
      if (payload.error && typeof payload.error === "object") {
        const nested = payload.error;
        if (
          typeof nested.error_user_msg === "string" &&
          nested.error_user_msg.trim()
        )
          return nested.error_user_msg;
        if (
          typeof nested.error_user_title === "string" &&
          nested.error_user_title.trim()
        )
          return nested.error_user_title;
        if (typeof nested.message === "string" && nested.message.trim())
          return nested.message;
      }

      return undefined;
    };

    // Função helper para ler o body uma única vez
    // IMPORTANTE: Sempre ler como texto primeiro, depois parsear
    // Isso evita problemas de "body stream already read"
    const readResponseBody = async (): Promise<any> => {
      // Se já lemos, retornar o resultado armazenado
      if (responseBody !== null) {
        console.log("✅ [API SERVICE] Retornando body do cache");
        return responseBody;
      }

      // Se o body já foi consumido, evitar nova leitura
      if (response.bodyUsed) {
        console.warn("⚠️ [API SERVICE] Response body já consumido");
        if (response.ok) {
          throw new Error("Falha ao ler corpo da resposta (body já consumido)");
        }
        responseBody = {
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
        return responseBody;
      }

      try {
        let text: string;

        // Tentar ler do clone primeiro (para não consumir o original)
        // Se o clone falhar (ex: conexão fechada), tentar ler diretamente
        try {
          const bodyResponse = response.clone();
          text = await bodyResponse.text();
        } catch (cloneError: any) {
          // Se o clone falhar, tentar ler diretamente do original
          // Isso pode acontecer se a conexão foi fechada prematuramente
          console.warn(
            "⚠️ [API SERVICE] Clone falhou, tentando ler diretamente:",
            cloneError?.message,
          );
          try {
            text = await response.text();
          } catch (directError: any) {
            // Se ambos falharem, é um erro de rede real
            console.error(
              "❌ [API SERVICE] Erro ao ler response (clone e direto):",
              directError,
            );
            throw directError;
          }
        }

        // Debug: verificar o conteúdo da resposta
        if (process.env.NODE_ENV === "production") {
          const contentType = response.headers.get("content-type") || "";
          console.log(
            "🔍 [API SERVICE] Response text (primeiros 200 chars):",
            text?.substring(0, 200),
          );
          console.log("🔍 [API SERVICE] Content-Type:", contentType);
          console.log(
            "🔍 [API SERVICE] Status:",
            response.status,
            response.statusText,
          );
        }

        // Se o texto estiver vazio, retornar objeto vazio
        if (!text || text.trim() === "") {
          console.warn("⚠️ [API SERVICE] Resposta vazia");
          responseBody = {};
          return responseBody;
        }

        // Tentar parsear como JSON
        try {
          responseBody = JSON.parse(text);
          console.log("✅ [API SERVICE] JSON parseado com sucesso");
          return responseBody;
        } catch (parseError) {
          console.error("❌ [API SERVICE] Erro ao parsear JSON:", parseError);
          console.error(
            "❌ [API SERVICE] Texto recebido (primeiros 500 chars):",
            text.substring(0, 500),
          );
          // Se não conseguir parsear e não for erro HTTP, retornar objeto vazio
          if (response.ok) {
            responseBody = {};
            return responseBody;
          }
          responseBody = { error: text };
          return responseBody;
        }
      } catch (e: any) {
        console.error("❌ [API SERVICE] Erro ao ler response.text():", e);
        console.error("❌ [API SERVICE] Erro name:", e?.name);
        console.error("❌ [API SERVICE] Erro message:", e?.message);
        const isNetworkOrFetch =
          e?.message?.includes("Failed to fetch") ||
          e?.message?.includes("network") ||
          e?.name === "TypeError";
        // Conexão fechada durante transferência (comum em produção com resposta grande/timeout)
        if (response.ok && isNetworkOrFetch) {
          throw new Error(
            "Conexão interrompida ao carregar os dados. Tente atualizar a página (F5) ou verifique se o servidor está acessível.",
          );
        }
        if (response.ok) {
          throw new Error("Falha ao ler corpo da resposta");
        }
        responseBody = {
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
        return responseBody;
      }
    };

    if (!response.ok) {
      // Ler o body do erro uma única vez
      const errorData = await readResponseBody();

      // Tratamento específico para erro 401 (não autorizado)
      if (response.status === 401 && config?.requiresAuth !== false) {
        const backendMsg = extractBackendErrorMessage(errorData);
        console.log("🔄 [API SERVICE] Token expirado, tentando renovar...");

        let refreshed = false;
        if (this.tokenRenewer) {
          refreshed = await this.tokenRenewer();
        } else {
          console.warn("⚠️ [API SERVICE] TokenRenewer não configurado");
        }

        if (refreshed) {
          console.log("✅ [API SERVICE] Token renovado com sucesso");
          // O retry será feito automaticamente por makeRequest
        } else {
          console.log(
            "❌ [API SERVICE] Falha ao renovar token, fazendo logout...",
          );
          clearAuthStorage();
          redirectToLogin("Sua sessão expirou. Faça login novamente.");
        }

        const appError = ErrorHandler.fromHttpResponse(response, errorData);
        const error = new Error(backendMsg || appError.userFriendlyMessage);
        if (refreshed) {
          (error as any).statusCode = 401;
          (error as any).tokenRefreshed = true;
        }
        throw error;
      }

      // Tratamento específico para créditos insuficientes (402)
      if (
        response.status === 402 ||
        errorData?.code === "insufficient_credits"
      ) {
        const creditsMessage =
          errorData?.message ||
          errorData?.error ||
          "Seus créditos acabaram. Acesse Configurações > Créditos para ver seu saldo e histórico.";
        showToastWarning(creditsMessage);
      }

      // Tratamento específico para backend indisponível
      if (response.status === 503) {
        if (errorData.error === "Backend indisponível") {
          // Só fazer logout se o usuário estiver realmente autenticado
          if (this.getAuthToken()) {
            console.log(
              "❌ [API SERVICE] Backend indisponível, fazendo logout do usuário autenticado...",
            );
            clearAuthStorage();
            redirectToLogin(
              "O servidor backend não está respondendo. Faça login novamente quando o servidor estiver disponível.",
            );

            const appError = ErrorHandler.fromHttpResponse(response, {
              error: "Servidor indisponível. Você foi desconectado.",
              message:
                "O servidor backend não está respondendo. Faça login novamente quando o servidor estiver disponível.",
            });
            throw new Error(appError.userFriendlyMessage);
          } else {
            console.log(
              "❌ [API SERVICE] Backend indisponível, mas usuário não está autenticado",
            );
            const appError = ErrorHandler.fromHttpResponse(response, {
              error: "Servidor indisponível.",
              message:
                "O servidor backend não está respondendo. Tente novamente mais tarde.",
            });
            throw new Error(appError.userFriendlyMessage);
          }
        }
      }

      const appError = ErrorHandler.fromHttpResponse(response, errorData);
      ErrorHandler.logError(appError, "ApiService.makeRequest");

      // Preservar mensagem de erro específica do backend quando disponível
      // Para erros de validação, incluir detalhes se disponíveis
      let errorMessage =
        extractBackendErrorMessage(errorData) || appError.userFriendlyMessage;

      // Tratamento específico para erro 429 (quota excedida)
      if (response.status === 429) {
        // Preservar a mensagem original do backend
        errorMessage =
          errorData?.error || errorData?.message || "Quota da API excedida";
      }
      // Se for erro de validação com detalhes, formatar melhor
      else if (
        response.status === 400 &&
        errorData?.details &&
        Array.isArray(errorData.details)
      ) {
        const validationErrors = errorData.details
          .map((detail: any) => {
            const field = detail.loc?.join(".") || detail.field || "campo";
            const msg = detail.msg || detail.message || "inválido";
            return `${field}: ${msg}`;
          })
          .join(", ");
        errorMessage = `Erro de validação: ${validationErrors}`;
      }
      // Se for erro 500, incluir detalhes se disponíveis
      else if (response.status >= 500 && errorData?.details) {
        const details =
          typeof errorData.details === "string"
            ? errorData.details
            : safeStringify(errorData.details);
        errorMessage = `${errorMessage} (Detalhes: ${details})`;
      }

      const error = new Error(errorMessage);
      // Adicionar dados adicionais ao erro para debug e tratamento específico
      (error as any).statusCode = response.status;
      (error as any).errorData = errorData;
      // Para erros 429, preservar também os detalhes completos
      if (response.status === 429 && errorData?.details) {
        (error as any).quotaDetails = errorData.details;
      }
      throw error;
    }

    // Para respostas de sucesso, ler o body
    const successData = await readResponseBody();
    return successData as T;
  }

  async makeRequest<T = any>(
    endpoint: string,
    method: string = "GET",
    data?: any,
    config: ApiConfig = {},
  ): Promise<T> {
    const {
      requiresAuth = true,
      timeout = TIMEOUTS.API_REQUEST,
      retry = false, // Desabilitado por padrão para evitar interferência no tratamento de erros específicos
    } = config;

    const url = this.buildUrl(endpoint);
    const headers = this.buildHeaders(requiresAuth);
    const requestConfig = this.buildConfig(method, headers, data, {
      ...config,
      timeout,
    });

    // Armazenar URL para uso no catch
    const requestUrl = url;

    try {
      let response: Response;

      if (retry) {
        // Usar retry automático
        response = await retryMechanism.fetchWithRetry(
          requestUrl,
          requestConfig,
          "ApiService",
        );
      } else {
        // Requisição sem retry
        response = await fetch(requestUrl, requestConfig);
      }

      try {
        // Não clonar - vamos ler apenas uma vez no handleResponse
        return await this.handleResponse<T>(response, config);
      } catch (readError: any) {
        // Se o token foi renovado, refazer a requisição automaticamente
        if (readError.statusCode === 401 && readError.tokenRefreshed) {
          console.log(
            "🔄 [API SERVICE] Refazendo requisição após renovar token...",
          );
          // Precisamos reconstruir os headers para pegar o NOVO token do localStorage
          const newHeaders = this.buildHeaders(requiresAuth);
          const newRequestConfig = this.buildConfig(method, newHeaders, data, {
            ...config,
            timeout,
          });
          const retryResponse = await fetch(requestUrl, newRequestConfig);
          return await this.handleResponse<T>(retryResponse, config);
        }

        const isReadBodyError = readError?.message?.includes(
          "Falha ao ler corpo da resposta",
        );
        const isSafeToRetry = method === "GET" && !retry; // Evitar retry duplo

        if (isReadBodyError && isSafeToRetry) {
          console.warn(
            "⚠️ [API SERVICE] Falha ao ler corpo, refazendo GET uma vez...",
            {
              url: requestUrl,
              method,
            },
          );
          const retryResponse = await fetch(requestUrl, requestConfig);
          return await this.handleResponse<T>(retryResponse, config);
        }

        throw readError;
      }
    } catch (error: any) {
      // Se já for um erro tratado (com statusCode), preservar a mensagem original
      // Isso inclui erros 429 (quota), 400 (validação), etc. que foram tratados em handleResponse
      // Não logar novamente pois já foi logado em handleResponse
      if (error.statusCode) {
        // Preservar a mensagem original e os dados do erro
        throw error;
      }

      // Log detalhado do erro para debug (apenas para erros não tratados) —
      // um único console.error por falha, não uma cascata de linhas (isso
      // dispara por requisição, e várias telas fazem Promise.all de 5-6
      // chamadas, então cada linha extra aqui vira uma parede de log).
      const isNetworkError =
        error.name === "TypeError" && error.message.includes("fetch");
      const isCorsError =
        error.message && error.message.toLowerCase().includes("cors");

      console.error("❌ [API SERVICE] Erro na requisição:", {
        url: requestUrl,
        method,
        error: error.message || String(error),
        errorName: error.name,
        isNetworkError,
        isCorsError,
        isTimeout: error.name === "AbortError",
        ...(isNetworkError && {
          diagnostico:
            "Backend fora do ar, porta incorreta, firewall, ou CORS não configurado — verifique http://localhost:8000/api/health",
        }),
      });

      // Tratamento de erros de rede/timeout
      if (error.name === "AbortError") {
        const appError = ErrorHandler.fromError(error);
        throw new Error(appError.userFriendlyMessage);
      }

      // Preservar mensagens já amigáveis (conexão interrompida, sessão, etc.)
      if (
        error.message &&
        (error.message.includes("Sessão expirada") ||
          error.message.includes("Conexão interrompida") ||
          error.message.includes("Falha ao ler corpo"))
      ) {
        throw error;
      }

      // Tratamento genérico de erros (apenas para erros que não passaram por handleResponse)
      const appError = ErrorHandler.fromError(error);
      throw new Error(appError.userFriendlyMessage);
    }
  }

  // ===== MÉTODOS DE CONVENIÊNCIA =====

  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    config?: ApiConfig,
  ): Promise<T> {
    // Filtra parâmetros nulos, indefinidos ou com a string literal 'undefined'
    const filteredParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(
            ([_, v]) =>
              v !== undefined &&
              v !== null &&
              v !== "" &&
              v !== "undefined" &&
              v !== "null",
          ),
        )
      : undefined;

    const hasParams = filteredParams && Object.keys(filteredParams).length > 0;
    const urlWithParams = hasParams
      ? `${endpoint}?${new URLSearchParams(filteredParams as any)}`
      : endpoint;
    return this.makeRequest<T>(urlWithParams, "GET", undefined, config);
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: ApiConfig,
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, "POST", data, config);
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: ApiConfig,
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, "PUT", data, config);
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: ApiConfig,
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, "PATCH", data, config);
  }

  async delete<T = any>(endpoint: string, config?: ApiConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, "DELETE", undefined, config);
  }

  // ===== MÉTODOS PARA UPLOAD DE ARQUIVOS =====

  async uploadFile<T = any>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>,
    config?: ApiConfig,
  ): Promise<T> {
    const { requiresAuth = true } = config || {};

    const url = this.buildUrl(endpoint);
    const headers = this.buildHeaders(requiresAuth);

    // Remover Content-Type para FormData
    delete headers["Content-Type"];

    const formData = new FormData();
    // Determinar a chave baseado no endpoint (video, image ou file)
    const fileKey = endpoint.includes("upload-video")
      ? "video"
      : endpoint.includes("upload-image")
        ? "image"
        : "file";
    formData.append(fileKey, file);

    // Adicionar dados adicionais se fornecidos
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    const requestConfig = this.buildConfig("POST", headers, formData, config);

    try {
      const response = await fetch(url, requestConfig);
      return await this.handleResponse<T>(response, config);
    } catch (error: any) {
      const appError = ErrorHandler.fromError(error);
      ErrorHandler.logError(appError, "ApiService.uploadFile");
      // Preservar mensagem do backend (ex.: limites de tamanho/formato) quando disponível
      throw new Error(error?.message || appError.userFriendlyMessage);
    }
  }

  // ===== MÉTODOS PARA DOWNLOAD DE ARQUIVOS =====

  async downloadFile(
    endpoint: string,
    filename?: string,
    config?: ApiConfig,
  ): Promise<void> {
    const { requiresAuth = true } = config || {};

    const url = this.buildUrl(endpoint);
    const headers = this.buildHeaders(requiresAuth);

    // Detectar se é localhost
    const isLocalhost = url.includes("localhost") || url.includes("127.0.0.1");

    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        // Em localhost, omitir credentials
        ...(isLocalhost ? {} : { credentials: "include" }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorData}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error: any) {
      const appError = ErrorHandler.fromError(error);
      ErrorHandler.logError(appError, "ApiService.downloadFile");
      throw new Error(appError.userFriendlyMessage);
    }
  }
}

// Instância singleton do serviço
const apiService = new ApiService();
export default apiService;

// Exportar também os endpoints para conveniência
export { API_ENDPOINTS };
